import { Divider } from "antd";
import type { TFunction } from "i18next";
import { TabsField } from "./tabs";
import formStyles from "@/components/DynamicForm/index.module.less";
import type { MaterialStatusOption } from "../hooks/useMaterialStatusOptions";
import type { BackendEvaluationNode } from "../hooks/useEvaluationCategoryTransform";

// ==================== 常量配置 ====================
const DEFAULT_MATERIAL_STATUS_OPTIONS: MaterialStatusOption[] = [];

// ==================== 评估表单配置 ====================
/**
 * 生成评估表单配置项
 * @param t - i18n 翻译函数
 * @param materialStatusOptions - 物料状态选项
 * @param evaluationCategoryData - 评估分类数据
 * @param onOpenSupplierModal - 打开供应商选择弹框的回调函数
 */
export const getEvaluationFormItems = (
  t: TFunction,
  materialStatusOptions: MaterialStatusOption[] = DEFAULT_MATERIAL_STATUS_OPTIONS,
  evaluationCategoryData?: BackendEvaluationNode[],
  onOpenSupplierModal?: () => void,
) => [
  {
    name: "custom1",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.sectionHeader}>
          <div className={formStyles.sectionIndicator} />
          <span className={formStyles.sectionTitle}>{t("申请人信息")}</span>
        </div>
      ),
    },
  },
  {
    name: "applicantWrapperStart",
    col: { span: 24 },
    wrapperStart: { className: formStyles.wrapperPadded },
  },
  {
    name: "purchaseUserId",
    col: { span: 0 },
    widget: {
      widgetType: "Input",
      hidden: true,
    },
  },
  {
    label: t("员工姓名"),
    name: "employeeName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    label: t("员工编号"),
    name: "employeeCode",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    label: t("创建日期"),
    name: "createDate",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    label: t("部门"),
    name: "department",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    name: "deptNo",
    col: { span: 0 },
    widget: {
      widgetType: "Input",
      hidden: true,
    },
  },
  {
    label: t("岗位"),
    name: "position",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [],
    widget: {
      widgetType: "Input",
      disabled: true,
    },
  },
  {
    name: "postNo",
    col: { span: 0 },
    widget: {
      widgetType: "Input",
      hidden: true,
    },
  },
  {
    label: t("邮箱"),
    name: "email",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [{ type: "email", message: t("邮箱格式不正确") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入"),
    },
  },
  {
    name: "applicantWrapperEnd",
    col: { span: 24 },
    wrapperEnd: true,
  },
  {
    name: "custom2",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.sectionHeaderWithAction}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>
              {t("供应商基本信息")}
            </span>
          </div>
        </div>
      ),
    },
  },
  {
    name: "supplierWrapperStart",
    col: { span: 24 },
    wrapperStart: { className: formStyles.wrapperLight },
  },
  {
    name: "supplierUserId",
    col: { span: 0 },
    widget: {
      widgetType: "Input",
      hidden: true,
    },
  },
  {
    label: t("供应商名称（中文）"),
    name: "primaryCategory",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请输入供应商名称") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入供应商名称"),
      readOnly: true,
      style: { cursor: "pointer" },
      onClick: () => onOpenSupplierModal?.(),
    },
  },
  {
    label: t("供应商名称（英文）"),
    name: "primaryCategoryEn",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请输入供应商名称") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入供应商名称"),
      readOnly: true,
      style: { cursor: "pointer" },
      onClick: () => onOpenSupplierModal?.(),
    },
  },
  {
    label: t("供应商状态"),
    name: "supplierStatus",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Select",
      placeholder: t("请输入"),
      options: [
        { label: t("新供应商"), value: "新供应商" },
        { label: t("现有供应商"), value: "现有供应商" },
      ],
    },
  },
  {
    label: t("物料状态"),
    name: "materialStatus",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Select",
      placeholder: t("请输入"),
      options: materialStatusOptions,
    },
  },
  {
    label: t("物料用途类别"),
    name: "materialUsageType",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Select",
      placeholder: t("请输入"),
      options: [
        { label: t("原料"), value: "原料" },
        { label: t("辅料"), value: "辅料" },
        { label: t("包装材料"), value: "包装材料" },
      ],
    },
  },
  {
    label: t("物料编号如有"),
    name: "materialCode",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入物料编号"),
    },
  },
  {
    label: t("物料名称"),
    name: "materialName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t("请输入") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入物料名称"),
    },
  },
  {
    label: t("注册市场"),
    name: "businessRegistration",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Select",
      placeholder: t("请选择"),
      options: [
        { label: t("中国内地"), value: "中国内地" },
        { label: t("中国香港"), value: "中国香港" },
        { label: t("美国"), value: "美国" },
        { label: t("其他"), value: "其他" },
      ],
    },
  },
  {
    label: t("产地区域"),
    name: "productionArea",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [{ required: true, message: t("请选择") }],
    widget: {
      widgetType: "Select",
      placeholder: t("请选择"),
      options: [
        { label: t("中国"), value: "中国" },
        { label: t("欧美"), value: "欧美" },
        { label: t("亚太"), value: "亚太" },
        { label: t("其他"), value: "其他" },
      ],
    },
  },
  {
    name: "applicantWrapperEnd",
    col: { span: 24 },
    wrapperEnd: true,
  },
  {
    name: "assessmentTabs",
    col: { span: 24 },
    custom: {
      render: () => <TabsField backendData={evaluationCategoryData} />,
    },
  },
  {
    name: "divider1",
    col: { span: 24 },
    custom: {
      render: () => <Divider style={{ margin: "24px 0" }} />,
    },
  },
  // {
  //   label: "审核意见",
  //   name: "reviewOpinion",
  //   col: { span: 24 },
  //   labelCol: { span: 2 },
  //   wrapperCol: { span: 20 },
  //   className: "inlineLabel",
  //   rules: [{ required: true, message: "请选择审核意见" }],
  //   widget: {
  //     widgetType: "Radio.Group",
  //     options: [
  //       { label: "同意", value: "agree" },
  //       { label: "驳回", value: "reject" },
  //     ],
  //   },
  // },
  // {
  //   label: "意见说明",
  //   name: "reviewComments",
  //   col: { span: 24 },
  //   labelCol: { span: 2 },
  //   wrapperCol: { span: 22 },
  //   className: "inlineLabel",
  //   rules: [{ required: true, message: "请输入意见说明" }],
  //   widget: {
  //     widgetType: "Input.TextArea",
  //     placeholder: "请输入...",
  //     rows: 4,
  //     style: { resize: "none" },
  //   },
  // },
];
